import { gql } from '@apollo/client';

export const AUTH_API_URL =
  import.meta.env.VITE_AUTH_API_URL || 'https://api-auth.butlr.io/graphql';

// Space types matching backend enum (5 levels: site > building > floor > room > zone)
export type SpaceType = 'site' | 'building' | 'floor' | 'room' | 'zone';

// Space relations matching backend enum (4 relations)
export type SpaceRelation = 'admin' | 'member' | 'can_edit' | 'can_view';

// Keep SpaceLevel/AccessLevel as aliases for backward compatibility in UI components
export type SpaceLevel = SpaceType;
export type AccessLevel = SpaceRelation;

// Space assignment as returned by the backend API
export interface SpaceAssignment {
  space_type: SpaceType;
  space_id: string;
  relation: SpaceRelation;
}

// Extended space assignment with resolved display info (for UI use)
export interface SpaceAssignmentDisplay extends SpaceAssignment {
  id: string; // Generated client-side key: `${space_type}-${space_id}-${relation}`
  spaceName: string;
  spacePath: string;
}

// Extended user interface with space assignments and last active
export interface UserWithAssignments {
  id: string;
  name: string;
  email: string;
  roles: string[];
  lastActiveAt?: string;
  spaceAssignments: SpaceAssignment[];
}

// Space option for dropdowns (sourced from main Butlr API, not auth API)
export interface SpaceOption {
  id: string;
  name: string;
  level: SpaceType;
  parentId?: string;
}

// --- GraphQL Query/Mutation Variables and Responses ---

export interface GetUserSpaceAssignmentsVariables {
  user_id: string;
}

export interface GetUserSpaceAssignmentsResponse {
  userSpaceAssignments: SpaceAssignment[];
}

export interface AssignUserToSpaceInput {
  user_id: string;
  space_type: SpaceType;
  space_id: string;
  relation: SpaceRelation;
}

export interface AssignUserToSpaceVariables {
  input: AssignUserToSpaceInput;
}

export interface AssignUserToSpaceResponse {
  assignUserToSpace: boolean;
}

export interface RemoveUserFromSpaceInput {
  user_id: string;
  space_type: SpaceType;
  space_id: string;
  relation: SpaceRelation;
}

export interface RemoveUserFromSpaceVariables {
  input: RemoveUserFromSpaceInput;
}

export interface RemoveUserFromSpaceResponse {
  removeUserFromSpace: boolean;
}

export interface UserAccessibleSpacesVariables {
  user_id: string;
  space_type: SpaceType;
  relation: SpaceRelation;
}

export interface UserAccessibleSpacesResponse {
  userAccessibleSpaces: string[];
}

// --- GraphQL Operations ---

export const GET_USER_SPACE_ASSIGNMENTS = gql`
  query GetUserSpaceAssignments($user_id: ID!) {
    userSpaceAssignments(user_id: $user_id) {
      space_type
      space_id
      relation
    }
  }
`;

export const GET_USER_ACCESSIBLE_SPACES = gql`
  query GetUserAccessibleSpaces($user_id: ID!, $space_type: SpaceType!, $relation: SpaceRelation!) {
    userAccessibleSpaces(user_id: $user_id, space_type: $space_type, relation: $relation)
  }
`;

export const ASSIGN_USER_TO_SPACE = gql`
  mutation AssignUserToSpace($input: AssignUserToSpaceInput!) {
    assignUserToSpace(input: $input)
  }
`;

export const REMOVE_USER_FROM_SPACE = gql`
  mutation RemoveUserFromSpace($input: RemoveUserFromSpaceInput!) {
    removeUserFromSpace(input: $input)
  }
`;

// Convert backend SpaceAssignment to display format (space name/path resolved externally)
export const toDisplayAssignment = (
  assignment: SpaceAssignment,
  spaceLookup?: Map<string, SpaceOption>,
): SpaceAssignmentDisplay => {
  const space = spaceLookup?.get(assignment.space_id);
  return {
    ...assignment,
    id: `${assignment.space_type}-${assignment.space_id}-${assignment.relation}`,
    spaceName: space?.name || assignment.space_id,
    spacePath: assignment.space_id,
  };
};

// Helper to get display text for relation
export const getAccessLevelDisplay = (relation: SpaceRelation): string => {
  switch (relation) {
    case 'admin':
      return 'Admin';
    case 'member':
      return 'Member';
    case 'can_edit':
      return 'Can Edit';
    case 'can_view':
      return 'Can View';
    default:
      return relation;
  }
};

// Helper to get display text for space type
export const getSpaceLevelDisplay = (level: SpaceType): string => {
  switch (level) {
    case 'site':
      return 'Site';
    case 'building':
      return 'Building';
    case 'floor':
      return 'Floor';
    case 'room':
      return 'Room';
    case 'zone':
      return 'Zone';
    default:
      return level;
  }
};
